import * as THREE from 'three';
import { BufferGeometryUtils } from 'three/examples/jsm/Addons.js';
import type { FabricResult, FabricSide, TextureInfo } from './typing.ts';

export function getUVScale(mesh: THREE.Mesh): number {
  const geometry = mesh.geometry;

  const positions = geometry.attributes.position?.array;
  const uvs = geometry.attributes.uv?.array;
  const indices = geometry.index?.array;

  // If any of the required attributes are not available, return a default value
  if (!positions || !uvs || !indices) {
    console.warn('Geometry attributes are missing or undefined.');
    return 1; // Default scale
  }

  let totalRatio = 0;
  let edgeCount = 0;

  for (let i = 0; i < indices.length; i += 3) {
    const idx1 = indices[i]! * 3, idx2 = indices[i + 1]! * 3; // 3D vertex indices
    const idxUV1 = indices[i]! * 2, idxUV2 = indices[i + 1]! * 2; // UV indices

    // Physical length
    const dx = positions[idx2]! - positions[idx1]!;
    const dy = positions[idx2 + 1]! - positions[idx1 + 1]!;
    const dz = positions[idx2 + 2]! - positions[idx1 + 2]!;
    const physLength = Math.sqrt(dx * dx + dy * dy + dz * dz);

    // UV length
    const du = uvs[idxUV2]! - uvs[idxUV1]!;
    const dv = uvs[idxUV2 + 1]! - uvs[idxUV1 + 1]!;
    const uvLength = Math.sqrt(du * du + dv * dv);

    if (uvLength > 0) {
      totalRatio += physLength / uvLength;
      edgeCount++;
    }
  }
  return edgeCount > 0 ? totalRatio / edgeCount : 1;
}

export interface UVBounds {
  uv: {
    min: {
      u: number;
      v: number
    };
    max: {
      u: number;
      v: number
    };
    width: number;
    height: number
  };
  physical: {
    width: number;
    height: number;
    scale: number
  };
  transformed?: boolean;
}

export function calculateUVBounds(mesh: THREE.Mesh): UVBounds | null {
  const uvs = mesh.geometry.attributes.uv;
  if (!uvs || !uvs.array) return null;

  const uvArray = uvs.array;
  let minU = Number.MAX_VALUE, maxU = -Number.MAX_VALUE;
  let minV = Number.MAX_VALUE, maxV = -Number.MAX_VALUE;

  for (let i = 0; i < uvs.count; i += 2) {
    const u = uvArray[i] as number;
    const v = uvArray[i + 1] as number;

    if (u < minU) minU = u;
    if (u > maxU) maxU = u;
    if (v < minV) minV = v;
    if (v > maxV) maxV = v;
  }

  const uvWidth = maxU - minU;
  const uvHeight = maxV - minV;
  const uvScale = getUVScale(mesh);

  return {
    uv: {
      min: { u: minU, v: minV },
      max: { u: maxU, v: maxV },
      width: uvWidth,
      height: uvHeight
    },
    physical: {
      width: uvWidth * uvScale,
      height: uvHeight * uvScale,
      scale: uvScale
    }
  };
}

export function createDoubleSidedGeometry(originalGeometry: THREE.BufferGeometry) {
  const geometry = new THREE.BufferGeometry();
  if (originalGeometry.attributes.position) {
    geometry.setAttribute('position', originalGeometry.attributes.position.clone());
  }
  if (originalGeometry.attributes.normal) {
    geometry.setAttribute('normal', originalGeometry.attributes.normal.clone());
  }
  if (originalGeometry.index) {
    geometry.index = originalGeometry.index.clone();
  }
  if (originalGeometry.attributes.uv) {
    geometry.setAttribute('uv', originalGeometry.attributes.uv.clone());
  }

  const backGeometry = geometry.clone();
  const mergedGeometry = BufferGeometryUtils.mergeGeometries(
    [geometry, backGeometry],
    true
  );

  return mergedGeometry;
}

function calculateTextureProperty(t: TextureInfo | undefined, bounds: ReturnType<typeof calculateUVBounds>) {
  if (!t || !bounds) {
    return;
  }
  const texture = t.texture.clone();
  // Calculate the scaling factor (convert centimeters to model units)
  // There is no clear unified standard, but most 3D modeling software and engines use meters as the default unit
  // Assume the model unit is meters
  const modelUnitsPerMM = bounds.physical.scale / 1000;
  const repeatX = bounds.physical.width / (t.width * modelUnitsPerMM) / Math.abs(bounds.uv.width);
  const repeatY = bounds.physical.height / (t.height * modelUnitsPerMM) / Math.abs(bounds.uv.height);
  texture.repeat.set(repeatX, repeatY);
  if (t.offset) {
    texture.offset.set(t.offset[0], t.offset[1]);
  }
  return texture;
}

function createSideTextureMaterial(side: FabricSide, bounds: UVBounds) {
  const baseColorMap = calculateTextureProperty(side.baseColor, bounds);
  const alphaMap = calculateTextureProperty(side.alpha, bounds);
  if(alphaMap){
    alphaMap.repeat.copy(baseColorMap!.repeat);
    alphaMap.offset.copy(baseColorMap!.offset);
  }
  const displacementMap = calculateTextureProperty(side.displacement, bounds);
  if(displacementMap){
    displacementMap.repeat.copy(baseColorMap!.repeat);
    displacementMap.offset.copy(baseColorMap!.offset);
  }
  const normalMap = calculateTextureProperty(side.normal, bounds);
  if(normalMap){
    normalMap.repeat.copy(baseColorMap!.repeat);
    normalMap.offset.copy(baseColorMap!.offset);
  }

  return new THREE.MeshStandardMaterial({
    map: baseColorMap,
    alphaMap,
    displacementMap,
    displacementScale: displacementMap && 0.0024,
    normalMap,
    normalScale: normalMap && new THREE.Vector2(0.2, 0.4),
    transparent: true,
  });

}

export function createTextureMaterial(fabric: FabricResult, bounds: UVBounds, color?: string): THREE.Material | [THREE.Material, THREE.Material] {
  const isDoubleSide = !fabric?.back;
  const frontMat = createSideTextureMaterial(fabric.front, bounds);
  frontMat.needsUpdate = true;
  color && frontMat.color.set(color);

  if (isDoubleSide) {
    frontMat.side = THREE.DoubleSide;
  }

  if (!fabric.back) {
    return frontMat;
  }

  const backMat = createSideTextureMaterial(fabric.back, bounds);
  backMat.side = THREE.BackSide;
  backMat.needsUpdate = true;
  color && backMat.color.set(color);

  return [frontMat, backMat];
}

export function isMesh(o: THREE.Object3D): o is THREE.Mesh {
  return (o as THREE.Mesh).isMesh;
}

export function isMeshStandardMaterial(material: THREE.Material): material is THREE.MeshStandardMaterial {
  return material instanceof THREE.MeshStandardMaterial;
}

export function flipY(buffer: Uint8Array, width: number, height: number) {
  for (let i = 0, j = height - 1; i < j; ++i, --j) {
    for (let k = 0; k < 4 * width; ++k) {
      const idxI = i * width * 4 + k;
      const idxJ = j * width * 4 + k;
      const temp = buffer[idxI]!;
      buffer[idxI] = buffer[idxJ]!;
      buffer[idxJ] = temp;
    }
  }
}

export async function until(condition: () => boolean, interval: number = 0): Promise<void> {
  return new Promise((resolve) => {
    const checkCondition = () => {
      if (condition()) {
        resolve();
      } else {
        setTimeout(checkCondition, interval);
      }
    };
    checkCondition();
  });
}